import { Button, Form, Input, message, Modal, Select } from "antd";
import { useTranslation } from "react-i18next";
import { DownOutlined } from "@ant-design/icons";
import { useParams, useNavigate } from "react-router-dom";
import "../../../shared/styles/AddProducts.css";
import React, { useEffect, useState, useCallback } from "react";
import { apiClient, getStorageUrl } from "../../../shared/api/AllApi";
import {
  invalidateCatalog,
  useCategoriesCatalog,
  useUnitsCatalog,
} from "../../../hooks/useCatalogData";
import { parseLocalizedName } from "../../../utils/localization";

const UpdateProducts = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [form] = Form.useForm();
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const { data: storeUnits } = useUnitsCatalog();
  const { data: categoriesRaw } = useCategoriesCatalog();
  const categories = categoriesRaw
    .map((category) => ({
      id: category.id,
      name: parseLocalizedName(category.name, i18n.language, "Unknown"),
      status: category.status,
    }))
    .filter((category) => category.status === "active");

  const [storeSubUnits, setStoreSubUnits] = useState([]);
  const [selectedSubUnit, setSelectedSubUnit] = useState(null);
  const [subUnitRatio, setSubUnitRatio] = useState(1);
  const [subUnitPrice, setSubUnitPrice] = useState(0);
  const [loadingProduct, setLoadingProduct] = useState(true);
  const [errorProduct, setErrorProduct] = useState(null);

  const showNameModal = useCallback(() => {
    setIsNameModalOpen(true);
  }, []);

  const handleNameModalOk = useCallback(() => {
    setIsNameModalOpen(false);
  }, []);

  const handleNameModalCancel = useCallback(() => {
    setIsNameModalOpen(false);
  }, []);

  // Define fetchSubUnits BEFORE the useEffect that uses it
  const fetchSubUnits = useCallback(async (unitId) => {
    if (unitId) {
      try {
        const response = await apiClient.get(`/units/${unitId}/sub`);
        setStoreSubUnits(response.data || []);
      } catch (error) {
        console.error("Error fetching sub units:", error);
        setStoreSubUnits([]);
      }
    } else {
      setStoreSubUnits([]);
    }
  }, []); // No dependencies for this useCallback, or ensure they are stable
  // Fetch product details and initialize form
  useEffect(() => {
    const fetchProductDetails = async () => {
      setLoadingProduct(true);
      setErrorProduct(null);
      try {
        const response = await apiClient.get(`/products/${id}`);
        const fetchedProduct = response.data;
        setProduct(fetchedProduct);
        setLoadingProduct(false);

        // Set initial values for the form after product and categories are loaded
        if (fetchedProduct) {
          setImageUrl(fetchedProduct.image_url || null);
          const nameObj = typeof fetchedProduct.name === "string"
            ? JSON.parse(fetchedProduct.name || "{}")
            : fetchedProduct.name || {};

          // Set fields values
          form.setFieldsValue({
            TitleKurdish: nameObj.en || "",
            TitleArabic: nameObj.ar || "",
            unit: fetchedProduct.unit_id,
            Price: fetchedProduct.price || "",
            sku: fetchedProduct.sku || "",
            barcode: fetchedProduct.barcode || "",
            weight: fetchedProduct.weight || 0,
            sub_unit_id: fetchedProduct.sub_unit_id,
            sub_unit_ratio: fetchedProduct.sub_unit_ratio,
            sub_unit_price: fetchedProduct.sub_unit_price,
            category_id: fetchedProduct.category_id,
          });

          // Call fetchSubUnits here, after it's defined
          fetchSubUnits(fetchedProduct.unit_id);
          setSelectedSubUnit(fetchedProduct.sub_unit_id);
          setSubUnitRatio(fetchedProduct.sub_unit_ratio || 1);
          setSubUnitPrice(fetchedProduct.sub_unit_price || 0);
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        setErrorProduct(error);
        setLoadingProduct(false);
      }
    };

    fetchProductDetails();
  }, [id, form, fetchSubUnits]); // Dependencies: id, form, and fetchSubUnits

  // Handle main unit change
  const handleUnitChange = useCallback((value) => {
    form.setFieldsValue({ sub_unit_id: undefined });
    setSelectedSubUnit(null);
    setSubUnitRatio(1);
    setSubUnitPrice(0);
    fetchSubUnits(value);
  }, [form, fetchSubUnits]);

  const handleSubUnitChange = useCallback((value) => {
    setSelectedSubUnit(value);
    const sub = storeSubUnits.find((s) => s.id === value);
    if (sub && sub.ratio) {
      setSubUnitRatio(Number(sub.ratio) || 1);
      if (form.getFieldValue('Price')) {
        setSubUnitPrice(Number(form.getFieldValue('Price')) / (Number(sub.ratio) || 1));
      }
    } else {
      setSubUnitRatio(1);
      setSubUnitPrice(Number(form.getFieldValue('Price')) || 0);
    }
  }, [storeSubUnits, form]);

  // Update subunit price when main price changes
  useEffect(() => {
    if (selectedSubUnit && form.getFieldValue('Price') && subUnitRatio > 0) {
      setSubUnitPrice(Number(form.getFieldValue('Price')) / Number(subUnitRatio));
    }
  }, [form, subUnitRatio, selectedSubUnit]);

  const handleImageUpload = useCallback(async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      };
      const response = await apiClient.post(
        "/upload-file",
        formData,
        config
      );
      if (response.status === 200) {
        setImageUrl(response.data.path);
      } else {
        message.error(t("failed to upload image"));
      }
    } catch (error) {
      message.error(t("failed to upload image"));
      console.error("Image upload error:", error);
    }
  }, [t]);

  const normFile = useCallback((e) => {
    return e?.fileList ? [e.file] : [];
  }, []);

  const onChangeUpload = useCallback(async (e) => {
    if (e?.target?.files?.[0]) {
      await handleImageUpload(e.target.files[0]);
    }
  }, [handleImageUpload]);

  const onFinish = useCallback(async (values) => {
    if (!product) return;
    try {
      const name = {
        en: form.getFieldValue("TitleKurdish"),
        ar: form.getFieldValue("TitleArabic"),
      };
      const updatedValues = {
        ...values,
        name: name,
        unit_id: values.unit,
        price: Number(values.Price) || 0,
        barcode: values.barcode?.trim() || null,
        image_url: imageUrl,
        category_id: values.category_id || null,
        sub_unit_id: values.sub_unit_id || null,
        sub_unit_ratio: values.sub_unit_ratio ? Number(values.sub_unit_ratio) : null,
        sub_unit_price: values.sub_unit_price ? Number(values.sub_unit_price) : null,
      };

      const response = await apiClient.put(
        `/products/${id}`,
        updatedValues
      );

      if (response.status === 200) {
        invalidateCatalog("products");
        message.success(t("successfully updated"));
        navigate("/GetProducts");
      } else {
        message.error(t("failed to update"));
      }
    } catch (error) {
      message.error(t("failed to update"));
      console.error("Update error:", error);
    }
  }, [form, id, imageUrl, navigate, t, product]);

  const layout = {
    labelCol: { span: 6 },
    wrapperCol: { span: 16 },
  };

  const onFinishFailed = useCallback(() => {
    message.error(t("error"));
  }, [t]);

  if (loadingProduct) {
    return <div>{t("loading product details")}...</div>;
  }

  if (errorProduct) {
    return <div>{t("error loading product details")}</div>;
  }

  if (!product) {
    return <div>{t("product not found")}</div>;
  }

  return (
    <div style={{ textAlign: "center", marginTop: "50px", direction: "rtl" }}>
      <h1 style={{ fontSize: "24px", marginBottom: "20px" }}>
        {t("update products")}
      </h1>

      <Form
        form={form}
        {...layout}
        style={{ fontSize: "18px" }}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16 }}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        {/* //todo this is image */}
        <div className="centered-form-item">
          <Form.Item
            name="Logo"
            valuePropName="fileList"
            getValueFromEvent={normFile}
          >
            <input
              id="single-image-upload"
              type="file"
              style={{ display: "none" }}
              onChange={onChangeUpload}
            />
            <label htmlFor="single-image-upload" style={{ width: "30%" }}>
              {t("upload image")}
            </label>
            {imageUrl && (
              <img
                src={getStorageUrl(imageUrl)}
                alt="Uploaded file preview"
                style={{ width: "100px", height: "100px", objectFit: "cover" }}
                className="image-preview"
              />
            )}
            {product?.image_url && !imageUrl && (
              <img
                src={getStorageUrl(product.image_url)}
                alt="Existing file preview"
                style={{ width: "100px", height: "100px", objectFit: "cover" }}
                className="image-preview"
              />
            )}
          </Form.Item>
        </div>

        {/* Name Modal Button */}
        <Form.Item name="name" label={t("name")}>
          <Button
            type="text"
            style={{
              border: "1px solid #ccc",
              borderRadius: "4px",
              width: "70%",
              fontSize: "16px",
              textAlign: "center",
              display: "inline-block",
              backgroundColor: "#f0f0f0",
              color: "#333",
              fontWeight: "bold",
              boxShadow: "2px 2px 4px rgba(0, 0, 0, 0.1)",
            }}
            onClick={showNameModal}
          >
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
              {t("enter name product")}
              <DownOutlined />
            </div>
          </Button>
          <Modal
            style={{ direction: "rtl" }}
            title={t("enter name product")}
            open={isNameModalOpen}
            onOk={handleNameModalOk}
            onCancel={handleNameModalCancel}
          >
            <Form.Item name="TitleKurdish" label={t("كوردى")}>
              <Input />
            </Form.Item>
            <Form.Item name="TitleArabic" label={t("arabic")}>
              <Input />
            </Form.Item>
          </Modal>
        </Form.Item>        {/* Category Select */}
        <Form.Item
          name="category_id"
          label={t("Category")}
        >
          <Select
            placeholder={t("Select category")}
            style={{ width: "70%", fontSize: "16px", textAlign: "center" }}
            onChange={(value) => {
              form.setFieldsValue({ category_id: value });
            }}
            defaultValue={product?.category_id}
          >
            {categories.map((category) => (
              <Select.Option key={category.id} value={category.id}>
                {category.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* Main Unit Select */}
        <Form.Item
          name="unit"
          label={<span style={{ fontSize: "18px", fontWeight: "bold" }}>{t("یەکە")}</span>}
          rules={[{ required: true, message: t("please select a unit") }]}
        >
          <Select
            placeholder={t("select the unit")}
            style={{ width: "70%", fontSize: "16px", textAlign: "center" }}
            onChange={handleUnitChange}
            value={form.getFieldValue('unit')}
            allowClear
          >
            {storeUnits.map((unitItem) => {
              const unitName = JSON.parse(unitItem.name || "{}").en || JSON.parse(unitItem.name || "{}").ar || unitItem.name || `Unit ${unitItem.id}`;
              return (
                <Select.Option key={unitItem.id} value={unitItem.id}>
                  {unitName}
                </Select.Option>
              );
            })}
          </Select>
        </Form.Item>

        {/* Price */}
        <Form.Item
          name="Price"
          label={<span style={{ fontSize: "18px", fontWeight: "bold" }}>{t("price")}</span>}
        >
          <Input
            style={{ width: "70%", fontSize: "16px", textAlign: "center" }}
            placeholder={t("أدخل السعر")}
          />
        </Form.Item>
        {/* SKU */}
        <Form.Item
          name="sku"
          label={<span style={{ fontSize: "18px", fontWeight: "bold" }}>{t("sku")}</span>}
        >
          <Input
            style={{ width: "70%", fontSize: "16px", textAlign: "center" }}
            placeholder={t("Type your sku")}
          />
        </Form.Item>

        <Form.Item
          name="barcode"
          label={<span style={{ fontSize: "18px", fontWeight: "bold" }}>{t("productBarcode")}</span>}
        >
          <Input
            style={{ width: "70%", fontSize: "16px", textAlign: "center" }}
            placeholder={t("productBarcodePlaceholder")}
          />
        </Form.Item>

        {/* Weight */}
        <Form.Item
          name="weight"
          label={<span style={{ fontSize: "18px", fontWeight: "bold" }}>{t("کێش")}</span>}
        >
          <Input
            type="number"
            style={{ width: "70%", fontSize: "16px", textAlign: "center" }}
            placeholder={t("أدخل الوزن بالكيلوغرام")}
            suffix="KG"
            min="0"
            step="0.01"
          />
        </Form.Item>

        {/* Subunit Select */}
        {storeSubUnits.length > 0 && (
          <Form.Item
            label={t("یەکەی ژێر")}
            name="sub_unit_id"
          >
            <Select
              placeholder={t("select sub unit")}
              style={{ width: "70%", fontSize: "16px", textAlign: "center" }}
              value={selectedSubUnit}
              onChange={handleSubUnitChange}
              allowClear
            >
              {storeSubUnits.map((sub) => {
                const subUnitName = JSON.parse(sub.name || "{}").en || JSON.parse(sub.name || "{}").ar || JSON.parse(sub.name || "{}").ku || sub.name || `Sub Unit ${sub.id}`;
                return (
                  <Select.Option key={sub.id} value={sub.id}>
                    {subUnitName}
                  </Select.Option>
                );
              })}
            </Select>
          </Form.Item>
        )}

        {/* Ratio */}
        {selectedSubUnit && (
          <Form.Item
            label={t("پێوانەی یەکە")}
            name="sub_unit_ratio"
            rules={[{ required: true, message: t("please enter sub unit ratio") }]}
          >
            <Input
              style={{ width: "70%", fontSize: "16px", textAlign: "center" }}
              value={form.getFieldValue('sub_unit_ratio')}
              onChange={(e) => {
                const value = Number(e.target.value);
                if (value > 0) {
                  setSubUnitRatio(value);
                  if (form.getFieldValue('Price')) {
                    setSubUnitPrice(Number(form.getFieldValue('Price')) / value);
                  }
                }
              }}
            />
          </Form.Item>
        )}

        {/* Price per subunit */}
        {selectedSubUnit && (
          <Form.Item
            label={t("نرخی یەک ژێر یەکە")}
            name="sub_unit_price"
          >
            <Input
              style={{ width: "70%", fontSize: "16px", textAlign: "center" }}
              value={subUnitPrice}
              onChange={(e) => setSubUnitPrice(e.target.value)}
            />
          </Form.Item>
        )}

        {/* Submit and Cancel Buttons */}
        <Form.Item label=".">
          <Button
            type="primary"
            htmlType="submit"
            style={{ backgroundColor: "green", borderColor: "green", fontSize: "16px" }}
          >
            {t("submit")}
          </Button>
          <span style={{ margin: "0 8px" }}></span>
          <Button
            style={{ backgroundColor: "red", borderColor: "red", fontSize: "16px" }}
            type="primary"
            danger
            onClick={() => navigate("/GetProducts")}
          >
            {t("cancel")}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default UpdateProducts;
